import { useCallback } from 'react';
import { useAuthStore } from '@/features/auth';
import { api } from '@/services/api';
import type { ThemeName } from '@/contexts/ThemeContext';

/**
 * Hook to sync theme with backend API
 * Automatically fetches user's theme preference on mount
 * Provides function to save theme to backend
 *
 * CRITICAL: Functions are wrapped in useCallback to prevent infinite render loops
 * in components that use these functions in useEffect dependency arrays.
 */
export const useThemeSync = () => {
  const { isAuthenticated } = useAuthStore();

  /**
   * Fetch user's theme preference from API
   * Memoized to prevent infinite loops when used in useEffect dependencies
   */
  const fetchTheme = useCallback(async (): Promise<ThemeName | null> => {
    if (!isAuthenticated) return null;

    try {
      const response = await api.get('/users/me/theme');
      if (response.data.success) {
        return response.data.data.theme as ThemeName;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch theme preference:', error);
      return null;
    }
  }, [isAuthenticated]); // Only recreate when authentication status changes

  /**
   * Save theme preference to API
   * Memoized to prevent unnecessary re-renders
   */
  const saveTheme = useCallback(async (theme: ThemeName): Promise<boolean> => {
    if (!isAuthenticated) return false;

    try {
      console.log('🎨 [ThemeSync] Attempting to save theme:', theme);
      const response = await api.patch('/users/me/theme', { theme });
      console.log('🎨 [ThemeSync] Theme saved successfully:', response.data);
      return response.data.success;
    } catch (error: any) {
      console.error('🎨 [ThemeSync] Failed to save theme preference:', error);
      console.error('🎨 [ThemeSync] Error response:', error.response?.data);
      console.error('🎨 [ThemeSync] Error status:', error.response?.status);
      return false;
    }
  }, [isAuthenticated]); // Only recreate when authentication status changes

  return {
    fetchTheme,
    saveTheme,
  };
};
